import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import './TamiratTakip.css';

const TamiratTakip = () => {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Sorgulama fonksiyonu
  const fetchRepairStatus = useCallback(async (repairCode) => {
    if (!/^\d{6}$/.test(repairCode)) {
      setError('Lütfen 6 basamaklı tamirat numaranızı giriniz.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/public/repair-status/${repairCode}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Bir hata oluştu.');
        return;
      }

      setResult(data);
    } catch (err) {
      setError('Sunucuya bağlanılamadı. Lütfen tekrar deneyiniz.');
    } finally {
      setLoading(false);
    }
  }, []);

  // URL parametresinden kodu oku ve otomatik sorgula
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl && /^\d{6}$/.test(codeFromUrl)) {
      setCode(codeFromUrl);
      fetchRepairStatus(codeFromUrl);
    }
  }, [searchParams, fetchRepairStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    await fetchRepairStatus(code);
  };

  const handleReset = () => {
    setCode('');
    setResult(null);
    setError('');
  };

  // Durum için renk ve ikon belirleme
  const getStatusStyle = (status) => {
    const styles = {
      'received': { color: '#6366f1', bg: '#eef2ff', icon: '📥' },
      'sent_to_workshop': { color: '#8b5cf6', bg: '#f5f3ff', icon: '🚚' },
      'at_workshop': { color: '#8b5cf6', bg: '#f5f3ff', icon: '🏭' },
      'in_progress': { color: '#f59e0b', bg: '#fffbeb', icon: '🔧' },
      'completed': { color: '#10b981', bg: '#ecfdf5', icon: '✅' },
      'returning_to_store': { color: '#06b6d4', bg: '#ecfeff', icon: '🔄' },
      'ready_for_pickup': { color: '#22c55e', bg: '#f0fdf4', icon: '🎉' },
      'delivered': { color: '#64748b', bg: '#f8fafc', icon: '✨' }
    };
    return styles[status] || { color: '#64748b', bg: '#f8fafc', icon: '📋' };
  };

  // Durum adımları
  const statusSteps = [
    { key: 'received', label: 'Teslim Alındı' },
    { key: 'sent_to_workshop', label: 'Atölyeye Gönderildi' },
    { key: 'in_progress', label: 'İşlemde' },
    { key: 'completed', label: 'Tamamlandı' },
    { key: 'ready_for_pickup', label: 'Teslime Hazır' },
    { key: 'delivered', label: 'Teslim Edildi' }
  ];

  const getCurrentStepIndex = (status) => {
    // returning_to_store durumu completed ile ready_for_pickup arasında
    if (status === 'returning_to_store') return 3.5;
    // at_workshop (atölye teslim aldı) gönderildi ile işlemde arasında
    if (status === 'at_workshop') return 1.5;
    const index = statusSteps.findIndex(s => s.key === status);
    return index >= 0 ? index : 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="tamirat-takip-container">
      {/* Sol Taraf - Video/Görsel */}
      <div className="tamirat-left">
        <video
          className="tamirat-video-bg"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
        <div className="tamirat-overlay"></div>
        <div className="tamirat-brand">
          <img src="/logo.png" alt="Akik Gümüş" className="brand-logo" />
        </div>
      </div>

      {/* Sağ Taraf - Form/Sonuç */}
      <div className="tamirat-right">
        <div className="tamirat-card">
          {!result ? (
            <>
              <div className="tamirat-header">
                <div className="logo-container">
                  <img src="/logo.png" alt="Akik Gümüş" className="tamirat-logo" />
                </div>
                <h1>Tamirat Takip</h1>
                <p>Fişinizde yazan 6 basamaklı tamirat numarasını girerek ürününüzün durumunu öğrenebilirsiniz.</p>
              </div>

              <form onSubmit={handleSubmit} className="tamirat-form">
                {error && (
                  <div className="error-message">
                    <span className="error-icon">!</span>
                    {error}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="code">Tamirat Numarası</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      id="code"
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      inputMode="numeric"
                      pattern="\d{6}"
                      autoFocus
                    />
                  </div>
                  <span className="input-hint">Fişinizde yazan 6 haneli numara</span>
                </div>

                <button
                  type="submit"
                  className={`submit-btn ${loading ? 'loading' : ''}`}
                  disabled={loading || code.length !== 6}
                >
                  {loading ? 'Sorgulanıyor...' : 'Sorgula'}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="result-header">
                <div className="result-status" style={{
                  backgroundColor: getStatusStyle(result.repair.status).bg,
                  color: getStatusStyle(result.repair.status).color
                }}>
                  <span className="status-icon">{getStatusStyle(result.repair.status).icon}</span>
                  <span className="status-text">{result.repair.statusLabel}</span>
                </div>
                <h2>Tamirat #{result.repair.code}</h2>
                <p className="customer-name">{result.repair.customerName}</p>
              </div>

              {/* İlerleme Çubuğu */}
              <div className="progress-tracker">
                {statusSteps.map((step, index) => {
                  const currentIndex = getCurrentStepIndex(result.repair.status);
                  const isCompleted = index < currentIndex;
                  const isCurrent = index === Math.floor(currentIndex);

                  return (
                    <div
                      key={step.key}
                      className={`progress-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                    >
                      <div className="step-dot">
                        {isCompleted ? '✓' : index + 1}
                      </div>
                      <span className="step-label">{step.label}</span>
                      {index < statusSteps.length - 1 && <div className="step-line" />}
                    </div>
                  );
                })}
              </div>

              {/* Ürün Bilgileri */}
              <div className="result-section">
                <h3>Ürünler</h3>
                <div className="items-list">
                  {result.repair.items.map((item, index) => (
                    <div key={index} className="item-card">
                      <span className="item-desc">{item.description || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tarih Bilgileri */}
              <div className="result-section">
                <div className="date-info">
                  <div className="date-item">
                    <span className="date-label">Teslim Tarihi</span>
                    <span className="date-value">{formatDate(result.repair.createdAt)}</span>
                  </div>
                  {result.repair.estimatedDelivery && (
                    <div className="date-item">
                      <span className="date-label">Tahmini Teslim</span>
                      <span className="date-value">{formatDate(result.repair.estimatedDelivery)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Durum Geçmişi */}
              {result.history && result.history.length > 0 && (
                <div className="result-section">
                  <h3>Durum Geçmişi</h3>
                  <div className="history-timeline">
                    {result.history.map((h, index) => (
                      <div key={index} className="history-item">
                        <div className="history-content">
                          <span className="history-status">{h.statusLabel}</span>
                          <span className="history-date">{formatDate(h.date)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button className="reset-btn" onClick={handleReset}>
                Yeni Sorgulama
              </button>
            </>
          )}
        </div>

        <footer className="tamirat-footer">
          <p>© 2025 <a href="https://www.qubite.net/" target="_blank" rel="noopener noreferrer">Qubite ERP ve Perakende Çözümleri</a></p>
        </footer>
      </div>
    </div>
  );
};

export default TamiratTakip;
